import { createEffect, createRoot, untrack } from "solid-js";
import { createStore } from "solid-js/store";
import { themeState } from "@/shared/stores/themeStore";
import { trackingConfig, trackingConfigLoaded } from "@/tracking/config";
import { postConsentReceipt } from "./consentReceipt";
import {
  CONSENT_POLICY_VERSION,
  DEFAULT_CONSENT,
  categoriesFrom,
  type ConsentCategories,
  type ConsentChoice,
  type ConsentListener,
  type ConsentRecord,
  type ConsentSnapshot,
  type ConsentSource,
  type ConsentState,
  type OptionalConsentCategory,
} from "./consentTypes";
import { clearConsent, clearTrackingStorage, readConsent, writeConsent } from "./consentStorage";

/**
 * The single source of truth for what the visitor has allowed. The banner and the settings panel write
 * to it; the tracking layer only reads it, and only through <c>subscribeToConsent</c>.
 *
 * <p>The stored decision is read synchronously at module load — no await, no network — because the
 * answer is needed before anything can decide whether to inject a container. There is therefore no
 * window in which state is "unknown but maybe granted": until a decision is read back, both optional
 * categories are off and <c>needsDecision()</c> is true.</p>
 *
 * <p>"Not answered yet" and "answered, necessary only" are deliberately different states with the same
 * grants. The first must show the banner; the second must not. Both must inject nothing.</p>
 */

/** Random id for a receipt. Not derived from anything about the visitor — it identifies the decision,
 *  not the person. */
function newReceiptId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Insecure origin or a browser without it — the fallback below is fine, this is not a secret.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function currentHostname(): string {
  try {
    return location.hostname;
  } catch {
    return "";
  }
}

/**
 * The version a decision made now is recorded against: the tenant's own, as the server delivered it,
 * and never below the floor this build ships. Before the server has answered it is the floor alone.
 */
function policyVersionInForce(): number {
  const configured = trackingConfig().consentPolicyVersion;
  const tenant = typeof configured === "number" && Number.isFinite(configured) ? configured : 0;
  return Math.max(CONSENT_POLICY_VERSION, tenant);
}

/** Categories that were granted and are not any more, so their traces can be removed now. */
function withdrawnCategories(
  before: ConsentCategories,
  after: ConsentCategories,
): OptionalConsentCategory[] {
  const withdrawn: OptionalConsentCategory[] = [];
  if (before.statistics && !after.statistics) withdrawn.push("statistics");
  if (before.marketing && !after.marketing) withdrawn.push("marketing");
  return withdrawn;
}

function createConsentStore() {
  const stored = readConsent();

  const [state, setState] = createStore<ConsentState>({
    record: stored,
    categories: stored?.categories ?? { ...DEFAULT_CONSENT },
    settingsOpen: false,
    persisted: true,
  });

  const listeners = new Set<ConsentListener>();

  const snapshot = (): ConsentSnapshot => ({
    decided: state.record !== null,
    categories: { ...state.categories },
    record: state.record,
  });

  function notify(next: ConsentSnapshot) {
    // A listener that throws must not take the rest of them down with it, and must not leave the
    // store mid-update: the tracking layer's teardown is one of these.
    for (const listener of [...listeners]) {
      try {
        listener(next);
      } catch (e) {
        console.warn("Consent listener failed:", e);
      }
    }
  }

  // The subscription bridge. Reading the snapshot inside the effect is what registers the dependency
  // on every field; the notification itself is untracked so a listener touching other stores does not
  // widen it.
  createEffect(() => {
    const next = snapshot();
    untrack(() => notify(next));
  });

  // The stored decision was read before the server answered, against the floor alone. Once the tenant's
  // configuration is in, a record older than the version it names is not consent to the current text —
  // the vendor list changed since — so it is dropped and the visitor is asked again, exactly as a raised
  // floor would have done at load.
  createEffect(() => {
    if (!trackingConfigLoaded()) return;
    const required = policyVersionInForce();
    const record = untrack(() => state.record);
    if (record && record.policyVersion < required) untrack(invalidate);
  });

  /** Drop a decision that no longer counts, and whatever the categories it granted left behind. */
  function invalidate(): void {
    const before = { ...state.categories };
    clearConsent();
    clearTrackingStorage(withdrawnCategories(before, DEFAULT_CONSENT));
    setState({ record: null, categories: { ...DEFAULT_CONSENT }, persisted: true });
  }

  /**
   * Listen for consent changes. The listener is called once immediately with the current state: the
   * tracking layer is loaded lazily and would otherwise wait for the NEXT change — which never comes
   * for a visitor who decided on an earlier visit. Returns the unsubscribe.
   */
  function subscribe(listener: ConsentListener): () => void {
    listeners.add(listener);
    try {
      untrack(() => listener(snapshot()));
    } catch (e) {
      console.warn("Consent listener failed:", e);
    }
    return () => {
      listeners.delete(listener);
    };
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Record a decision and persist it. Returns the receipt that was written. */
  function accept(choice: ConsentChoice, source: ConsentSource): ConsentRecord {
    const categories = categoriesFrom(choice);
    const withdrawn = withdrawnCategories(untrack(() => state.categories), categories);

    const record: ConsentRecord = {
      policyVersion: policyVersionInForce(),
      decidedAt: new Date().toISOString(),
      categories,
      // The tenant resolved from the host server-side. Absent a loaded config this is the default
      // brand; hostname is recorded alongside precisely so the receipt is still unambiguous.
      tenant: themeState.tenant.id,
      hostname: currentHostname(),
      source,
      consentReceiptId: newReceiptId(),
    };

    const persisted = writeConsent(record);
    if (withdrawn.length > 0) clearTrackingStorage(withdrawn);

    // Fire and forget: the decision is already made and stored locally. This is the copy the server
    // can be asked about later, and a failed post must not turn a choice into an error.
    postConsentReceipt(record);

    // Saving is what closes the settings panel — there is no separate "apply".
    setState({ record, categories, persisted, settingsOpen: false });
    return record;
  }

  const acceptAll = (source: ConsentSource = "banner"): ConsentRecord =>
    accept({ statistics: true, marketing: true }, source);

  /** "Kun nødvendig" — a real, recorded decision, not a postponed one. The banner stays away. */
  const rejectAll = (source: ConsentSource = "banner"): ConsentRecord =>
    accept({ statistics: false, marketing: false }, source);

  /**
   * Withdraw entirely: the receipt is deleted, everything the granted categories left behind is
   * removed, and the visitor is back to being asked. Deliberately different from <c>rejectAll</c>,
   * which is an answer; this erases the answer.
   */
  function revoke(): void {
    clearConsent();
    clearTrackingStorage(["statistics", "marketing"]);
    setState({
      record: null,
      categories: { ...DEFAULT_CONSENT },
      persisted: true,
      settingsOpen: false,
    });
  }

  return {
    state,
    hasStatistics: () => state.categories.statistics,
    hasMarketing: () => state.categories.marketing,
    /** No valid decision on file: show the banner, and load nothing beyond the necessary category. */
    needsDecision: () => state.record === null,
    settingsOpen: () => state.settingsOpen,
    record: () => state.record,
    accept,
    acceptAll,
    rejectAll,
    revoke,
    subscribe,
    openSettings: () => setState("settingsOpen", true),
    closeSettings: () => setState("settingsOpen", false),
  };
}

// ── Singleton ────────────────────────────────────────────────────────────────

const consentStore = createRoot(createConsentStore);

export const consentState = consentStore.state;
export const hasStatistics = consentStore.hasStatistics;
export const hasMarketing = consentStore.hasMarketing;
export const needsDecision = consentStore.needsDecision;
export const consentSettingsOpen = consentStore.settingsOpen;
export const consentRecord = consentStore.record;
export const accept = consentStore.accept;
export const acceptAll = consentStore.acceptAll;
export const rejectAll = consentStore.rejectAll;
export const revoke = consentStore.revoke;
export const openSettings = consentStore.openSettings;
export const closeSettings = consentStore.closeSettings;

/** The tracking layer's only entry point into consent. See <c>subscribe</c> above. */
export const subscribeToConsent = consentStore.subscribe;
